import React, { useState, useEffect, useCallback } from 'react';
import * as XLSX from 'xlsx';

const API_URL = process.env.REACT_APP_API_URL || '';

function authFetch(url, opts) {
  const token = localStorage.getItem('wc_token');
  return fetch(url, {
    ...opts,
    headers: {
      ...(opts && opts.headers ? opts.headers : {}),
      ...(token ? { Authorization: 'Bearer ' + token } : {})
    }
  });
}

const ESTADO_CAMPANA_COLORS = { borrador: '#f59e0b', enviando: '#3b82f6', completada: '#22c55e', fallida: '#ef4444' };
const ESTADO_ENVIO_COLORS = { enviado: '#22c55e', fallido: '#ef4444', pendiente: '#f59e0b' };

// ═══ MAPEO COLUMNAS EXCEL → CRM ═══
const COL_ALIASES = {
  telefono: ['telefono','celular','tel','phone','telefonos celulares','telefonos_celulares','celulares','nro_celular','numero'],
  nombre: ['nombre','nombre_cliente','name','cliente'],
  apellido: ['apellido','apellido_cliente','last_name'],
  '1er_vto': ['1er_vto','primer_vencimiento','vencimiento','1er vencimiento','primer_vto','1er vto','fecha_vencimiento'],
  '2do_vto': ['2do_vto','segundo_vencimiento','segundo vencimiento','2do vencimiento','2do_vencimiento','segundo_vto','2do vto'],
  fecha_suspension: ['fecha_suspension','bloqueo','suspension','fecha_bloqueo','fecha_corte'],
  monto: ['monto','saldo','importe','deuda','total','amount'],
  customer_id: ['customer_id','codigo_cliente','id_cliente','codigo','id'],
  oferta: ['oferta','propuesta','descuento','beneficio'],
  motivo: ['motivo','razon','causa','detalle_motivo'],
  detalle: ['detalle','descripcion','mensaje','texto','aviso'],
  asesor: ['asesor','agente','vendedor','ejecutivo'],
  ciudad: ['ciudad','localidad','zona','region'],
  variable_1: ['variable_1','var_1','variable1'],
  variable_2: ['variable_2','var_2','variable2'],
  variable_3: ['variable_3','var_3','variable3'],
  dni: ['dni','documento','cuil','cuit','nro_externo'],
  email: ['email','mail','correo','correos','e-mail'],
  template: ['template','hito','plantilla','tipo'],
};

// variable_N de Bircle → campo CRM según hito (nombres del backend)
const VAR_MAP = {
  hito_2_dash: { variable_1:'1er_vto', variable_2:'2do_vto' },
  hito_3_dash: { variable_1:'1er_vto' },
  hito_4_dash: {},
  hito_5_dash: { variable_1:'2do_vto', variable_2:'fecha_suspension' },
  hito_6_dash: { variable_1:'fecha_suspension' },
  hito_7_dash: {},
  hito_8_dash: { variable_1:'oferta' },
  hito_9_dash: { variable_1:'oferta' },
  hito_10_dash: {},
  ciclo_vigente_dash: { variable_1:'1er_vto', variable_2:'2do_vto', variable_3:'fecha_suspension' },
  extra_reconexion_dash: { variable_1:'oferta' },
  extra_volver_dash: { variable_1:'asesor', variable_2:'ciudad' },
  tareas_mantenimiento_dash: { variable_1:'motivo' },
  corte_electrico_dash: {},
  aviso_general_dash: { variable_1:'detalle' },
};

function detectCol(h) {
  var norm = (h||'').trim().toLowerCase().replace(/\s+/g,' ');
  for (var f in COL_ALIASES) { if (COL_ALIASES[f].indexOf(norm) !== -1) return f; }
  return null;
}

// ═══ PARSEAR ARCHIVO ═══
function parseFile(file, hito, cb) {
  var ext = (file.name||'').split('.').pop().toLowerCase();

  if (ext === 'xlsx' || ext === 'xls' || ext === 'xlsm') {
    var reader = new FileReader();
    reader.onload = function(ev) {
      try {
        var wb = XLSX.read(ev.target.result, { type:'array', cellDates:true });
        var ws = wb.Sheets[wb.SheetNames[0]];
        var raw = XLSX.utils.sheet_to_json(ws, { header:1, defval:'' });
        if (raw.length < 2) { cb({ error:'Archivo vacío o sin datos' }); return; }

        // Detectar fila headers: si fila 0 tiene pocas celdas, headers están en fila 1
        var hIdx = 0;
        var ne0 = raw[0].filter(function(v){return v&&String(v).trim();}).length;
        var ne1 = raw.length > 1 ? raw[1].filter(function(v){return v&&String(v).trim();}).length : 0;
        if (ne1 > ne0 * 2 && ne0 <= 3 && raw.length > 2) hIdx = 1;

        var headers = raw[hIdx].map(function(h){return String(h||'').trim();});
        var colMap = {};
        headers.forEach(function(h,i){ var f=detectCol(h); if(f) colMap[i]={orig:h,field:f}; });

        var varMap = VAR_MAP[hito]||{};
        var hasApellido = Object.values(colMap).some(function(m){return m.field==='apellido';});
        var hasTemplateCol = Object.values(colMap).some(function(m){return m.field==='template';});
        // hito_5_dash → hito_5 para comparar con columna template del Excel
        var hitoSinDash = (hito||'').replace(/_dash$/,'');
        var rows = [];
        var filteredOut = 0;
        var totalConTel = 0;

        for (var r = hIdx+1; r < raw.length; r++) {
          var rr = raw[r];
          if (!rr.some(function(v){return v&&String(v).trim();})) continue;
          var fila = {}, apel = '';
          // Primero pasada: leer todas las columnas normales
          for (var c = 0; c < rr.length; c++) {
            var m = colMap[c]; if (!m) continue;
            var val = rr[c];
            var sv = val instanceof Date ? val.toLocaleDateString('es-AR',{day:'2-digit',month:'2-digit',year:'numeric'}) : String(val||'').trim();
            if (m.field === 'apellido') { apel = sv; continue; }
            // Saltar variables, se procesan después para que tengan prioridad
            if (m.field==='variable_1'||m.field==='variable_2'||m.field==='variable_3') continue;
            fila[m.field] = sv;
          }
          // Segunda pasada: variables SIEMPRE sobreescriben (tienen el formato del template)
          for (var c2 = 0; c2 < rr.length; c2++) {
            var m2 = colMap[c2]; if (!m2) continue;
            if (m2.field==='variable_1'||m2.field==='variable_2'||m2.field==='variable_3') {
              var val2 = rr[c2];
              var sv2 = val2 instanceof Date ? val2.toLocaleDateString('es-AR',{day:'2-digit',month:'2-digit',year:'numeric'}) : String(val2||'').trim();
              var tf = varMap[m2.field];
              if (tf && sv2) fila[tf] = sv2;
            }
          }
          if (hasApellido && apel && fila.nombre) fila.nombre = (fila.nombre+' '+apel).trim();
          if (!fila.telefono || String(fila.telefono).replace(/\D/g,'').length < 6) continue;
          totalConTel++;
          // Filtrar por template si la columna existe y el Excel tiene varios hitos
          if (hasTemplateCol && fila.template && fila.template !== hitoSinDash) {
            filteredOut++;
            continue;
          }
          // No mandar el campo template al CRM, no es dato del envío
          delete fila.template;
          rows.push(fila);
        }

        var mapped = Object.values(colMap).map(function(m){return m.orig+' → '+m.field;});
        var filterMsg = filteredOut > 0 ? ' (filtradas ' + filteredOut + ' filas de otros hitos)' : '';
        cb({ rows:rows, mappedColumns:mapped, totalRaw:raw.length-hIdx-1, parsed:rows.length, filterMsg:filterMsg });
      } catch(err) { cb({ error:'Error leyendo Excel: '+err.message }); }
    };
    reader.readAsArrayBuffer(file);

  } else {
    // CSV/TSV
    var csvR = new FileReader();
    csvR.onload = function(ev) {
      var text = ev.target.result;
      var lines = text.split('\n').filter(function(l){return l.trim();});
      if (lines.length < 2) { cb({ error:'Archivo vacío' }); return; }
      var sep = lines[0].indexOf('\t')!==-1 ? '\t' : lines[0].indexOf(';')!==-1 ? ';' : ',';
      var headers = lines[0].split(sep).map(function(h){return h.trim().replace(/"/g,'').replace(/\r/g,'');});
      var colMap = {};
      headers.forEach(function(h,i){ var f=detectCol(h); if(f) colMap[i]={orig:h,field:f}; });
      var varMap = VAR_MAP[hito]||{};
      var hasAp = Object.values(colMap).some(function(m){return m.field==='apellido';});
      var hasTemplateCol = Object.values(colMap).some(function(m){return m.field==='template';});
      var hitoSinDash = (hito||'').replace(/_dash$/,'');
      var rows = [];
      var filteredOut = 0;
      for (var i=1;i<lines.length;i++) {
        var vals = lines[i].split(sep).map(function(v){return v.trim().replace(/"/g,'').replace(/\r/g,'');});
        var fila = {}, apel = '';
        // Primero: columnas normales
        vals.forEach(function(v,idx){
          var m=colMap[idx]; if(!m) return;
          if(m.field==='apellido'){apel=v;return;}
          if(m.field==='variable_1'||m.field==='variable_2'||m.field==='variable_3') return;
          fila[m.field]=v;
        });
        // Segundo: variables sobreescriben (formato del template)
        vals.forEach(function(v,idx){
          var m=colMap[idx]; if(!m) return;
          if(m.field==='variable_1'||m.field==='variable_2'||m.field==='variable_3'){
            var tf=varMap[m.field]; if(tf&&v&&v.trim()) fila[tf]=v.trim();
          }
        });
        if(hasAp&&apel&&fila.nombre) fila.nombre=(fila.nombre+' '+apel).trim();
        if(!fila.telefono||String(fila.telefono).replace(/\D/g,'').length<6) continue;
        if(hasTemplateCol&&fila.template&&fila.template!==hitoSinDash){filteredOut++;continue;}
        delete fila.template;
        rows.push(fila);
      }
      var mapped = Object.values(colMap).map(function(m){return m.orig+' → '+m.field;});
      var filterMsg = filteredOut > 0 ? ' (filtradas ' + filteredOut + ' filas de otros hitos)' : '';
      cb({ rows:rows, mappedColumns:mapped, totalRaw:lines.length-1, parsed:rows.length, filterMsg:filterMsg });
    };
    csvR.readAsText(file);
  }
}

// ═══ PANEL PRINCIPAL ═══
function OutboundPanel() {
  var [tab, setTab] = useState('nueva');
  var [campanas, setCampanas] = useState([]);
  var [hitosConfig, setHitosConfig] = useState(null);
  var [loading, setLoading] = useState(false);

  var cargarDatos = useCallback(async function() {
    setLoading(true);
    try {
      var res = await Promise.all([
        authFetch(API_URL+'/api/outbound/campanas').then(function(r){return r.json();}),
        authFetch(API_URL+'/api/outbound/hitos-config').then(function(r){return r.json();})
      ]);
      if(res[0].ok)setCampanas(res[0].campanas||[]);
      if(res[1].ok)setHitosConfig(res[1].hitos||null);
    } catch(e){console.error('Error outbound:',e);}
    setLoading(false);
  },[]);

  useEffect(function(){cargarDatos();},[cargarDatos]);

  var tabs=[{id:'nueva',label:'+ Nueva Campaña'},{id:'campanas',label:'Campañas ('+campanas.length+')'},{id:'hitos',label:'Referencia Hitos'}];

  return (
    <div className="marketing-page">
      <div className="marketing-header">
        <div>
          <h1 style={{fontSize:24,letterSpacing:'-0.03em',margin:'0 0 5px'}}>Campañas Outbound</h1>
          <p style={{color:'var(--muted-2)',margin:0}}>Envío masivo de templates WhatsApp — cobranza, avisos y reconexión</p>
        </div>
        <div style={{display:'flex',gap:8}}>
          <button className="mkt-btn" onClick={cargarDatos} disabled={loading}>{loading?'Cargando...':'Actualizar'}</button>
        </div>
      </div>
      <div className="mkt-tabs">
        {tabs.map(function(t){return <button key={t.id} className={'mkt-tab '+(tab===t.id?'active':'')} onClick={function(){setTab(t.id);}}>{t.label}</button>;})}
      </div>
      {tab==='nueva'&&<NuevaCampTab hitosConfig={hitosConfig} onReload={cargarDatos} onCreada={function(){setTab('campanas');cargarDatos();}}/>}
      {tab==='campanas'&&<CampanasTab campanas={campanas} onReload={cargarDatos}/>}
      {tab==='hitos'&&<HitosTab hitosConfig={hitosConfig}/>}
    </div>
  );
}

// ═══ NUEVA CAMPAÑA ═══
function NuevaCampTab({hitosConfig,onReload,onCreada}){
  var [paso,setPaso]=useState(1);
  var [hito,setHito]=useState('');
  var [nombre,setNombre]=useState('');
  var [filas,setFilas]=useState([]);
  var [errores,setErrores]=useState([]);
  var [enviando,setEnviando]=useState(false);
  var [progreso,setProgreso]=useState(null);
  var [mapInfo,setMapInfo]=useState(null);

  var grupos={
    cobranza:{label:'Ciclo de cobranza',color:'#6366f1',hitos:['hito_2_dash','hito_3_dash','hito_4_dash','hito_5_dash','hito_6_dash','hito_7_dash','hito_8_dash','hito_9_dash','hito_10_dash','ciclo_vigente_dash']},
    reconexion:{label:'Reconexión',color:'#22c55e',hitos:['extra_reconexion_dash','extra_volver_dash']},
    avisos:{label:'Avisos operativos',color:'#f59e0b',hitos:['tareas_mantenimiento_dash','corte_electrico_dash','aviso_general_dash']}
  };

  // PASO 1
  if(paso===1) return (
    <div>
      <div style={{marginBottom:16}}>
        <label style={{fontSize:13,color:'var(--muted)',display:'block',marginBottom:6}}>Nombre de la campaña (opcional)</label>
        <input className="mkt-input" placeholder="Ej: Cobranza Junio 2026 — Hito 5" value={nombre} onChange={function(e){setNombre(e.target.value);}} style={{maxWidth:400}}/>
      </div>
      <label style={{fontSize:13,color:'var(--muted)',display:'block',marginBottom:10}}>Seleccioná el hito a enviar</label>
      {Object.entries(grupos).map(function(e){var k=e[0],g=e[1];return(
        <div key={k} style={{marginBottom:20}}>
          <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:10}}>
            <div style={{width:3,height:14,borderRadius:2,background:g.color}}></div>
            <span style={{fontSize:11,fontWeight:600,color:'var(--muted)',textTransform:'uppercase',letterSpacing:1}}>{g.label}</span>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill, minmax(280px, 1fr))',gap:8}}>
            {g.hitos.map(function(h){var c=hitosConfig?hitosConfig[h]:null;if(!c)return null;var s=hito===h;return(
              <div key={h} onClick={function(){setHito(h);}} style={{padding:'12px 16px',borderRadius:10,border:s?'1px solid '+g.color:'1px solid var(--border-light)',background:s?g.color+'12':'var(--card)',cursor:'pointer',transition:'all 0.15s'}}>
                <div style={{fontSize:13,fontWeight:600,marginBottom:4}}>{c.nombre}</div>
                <div style={{display:'flex',gap:4,flexWrap:'wrap'}}>
                  {c.tiene_boton_pago&&<span style={{fontSize:10,padding:'2px 6px',borderRadius:4,background:'rgba(34,197,94,.1)',color:'#4ade80',border:'1px solid rgba(34,197,94,.2)',fontWeight:600}}>Botón pago</span>}
                  <span style={{fontSize:10,padding:'2px 6px',borderRadius:4,background:'rgba(99,102,241,.12)',color:'#818cf8',fontWeight:600}}>{c.categoria_meta}</span>
                </div>
              </div>
            );})}
          </div>
        </div>
      );})}
      <div style={{marginTop:20,display:'flex',justifyContent:'flex-end'}}>
        <button className="mkt-btn primary" disabled={!hito} onClick={function(){setPaso(2);}}>Siguiente: Subir archivo →</button>
      </div>
    </div>
  );

  // PASO 2
  if(paso===2){
    var cfg2=hitosConfig?hitosConfig[hito]:null;
    var colsReq=cfg2?cfg2.columnas_requeridas:[];

    function handleFile(e){
      var file=e.target.files[0];if(!file)return;
      parseFile(file,hito,function(res){
        if(res.error){alert(res.error);return;}
        if(res.rows.length===0){alert('No se encontraron filas con teléfono en el archivo');return;}
        setMapInfo({cols:res.mappedColumns,totalRaw:res.totalRaw,parsed:res.parsed,filterMsg:res.filterMsg||''});
        authFetch(API_URL+'/api/outbound/validar-csv',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({filas:res.rows,hito:hito})})
        .then(function(r){return r.json();})
        .then(function(d){
          setFilas(d.filas_limpias&&d.filas_limpias.length>0?d.filas_limpias:res.rows);
          setErrores(d.errores||[]);
          setPaso(3);
        })
        .catch(function(){setFilas(res.rows);setErrores([]);setPaso(3);});
      });
    }

    return (
      <div>
        <button className="mkt-btn" onClick={function(){setPaso(1);}} style={{marginBottom:16}}>← Volver</button>
        <div className="mkt-card" style={{padding:20,marginBottom:16}}>
          <h3 style={{fontSize:15,marginBottom:8}}>Hito: <span style={{color:'var(--accent)'}}>{cfg2?cfg2.nombre:hito}</span></h3>
          <div style={{fontSize:12,color:'var(--muted)',marginBottom:12}}>Columnas que usa este template:</div>
          <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
            {colsReq.map(function(col){var b=cfg2&&cfg2.columnas_base.includes(col);return <span key={col} style={{fontSize:12,padding:'4px 10px',borderRadius:6,fontWeight:500,background:b?'rgba(99,102,241,.15)':'rgba(234,179,8,.1)',color:b?'#a5b4fc':'#fbbf24',border:b?'1px solid rgba(99,102,241,.25)':'1px solid rgba(234,179,8,.2)'}}>{col}</span>;})}
          </div>
        </div>
        <div className="mkt-card" style={{padding:40,textAlign:'center',border:'2px dashed var(--border-light)',borderRadius:12,cursor:'pointer',position:'relative'}}>
          <div style={{fontSize:36,marginBottom:12}}>📊</div>
          <div style={{fontSize:14,fontWeight:600,marginBottom:6}}>Arrastrá o hacé clic para subir el archivo</div>
          <div style={{fontSize:12,color:'var(--muted)'}}>Formatos: <strong>.xlsx</strong> (Excel), .csv, .tsv — columnas se mapean automáticamente</div>
          <input type="file" accept=".xlsx,.xls,.xlsm,.csv,.tsv,.txt" onChange={handleFile} style={{position:'absolute',inset:0,opacity:0,cursor:'pointer'}}/>
        </div>
      </div>
    );
  }

  // PASO 3
  if(paso===3){
    var cfg3=hitosConfig?hitosConfig[hito]:null;
    var cols=cfg3?cfg3.columnas_requeridas:(filas.length>0?Object.keys(filas[0]).filter(function(k){return k!=='_errores'&&k!=='telefono_normalizado'&&k!=='telefonos_normalizados'&&k!=='cantidad_telefonos';}):[]); 
    var nErr=filas.filter(function(f){return f._errores&&f._errores.length>0;}).length;

    function edit(idx,col,val){setFilas(filas.map(function(f,i){if(i!==idx)return f;var n=Object.assign({},f);n[col]=val;if(n._errores)n._errores=n._errores.filter(function(e){return e!==col;});return n;}));}

    return (
      <div>
        <button className="mkt-btn" onClick={function(){setPaso(2);setFilas([]);setMapInfo(null);}} style={{marginBottom:16}}>← Volver</button>
        {mapInfo&&<div style={{padding:'10px 16px',background:'rgba(99,102,241,.08)',border:'1px solid rgba(99,102,241,.15)',borderRadius:8,marginBottom:16,fontSize:11,color:'#a5b4fc'}}>✅ {mapInfo.parsed} filas cargadas de {mapInfo.totalRaw}{mapInfo.filterMsg} — Mapeo: {mapInfo.cols.join(', ')}</div>}
        <div style={{display:'flex',gap:12,marginBottom:16}}>
          <div className="mkt-kpi-card" style={{flex:1}}><span className="mkt-kpi-label">Total</span><strong className="mkt-kpi-value" style={{color:'var(--accent)'}}>{filas.length}</strong></div>
          <div className="mkt-kpi-card" style={{flex:1}}><span className="mkt-kpi-label">OK</span><strong className="mkt-kpi-value" style={{color:'var(--green)'}}>{filas.length-nErr}</strong></div>
          <div className="mkt-kpi-card" style={{flex:1}}><span className="mkt-kpi-label">Errores</span><strong className="mkt-kpi-value" style={{color:nErr>0?'var(--red)':'var(--green)'}}>{nErr}</strong></div>
          <div className="mkt-kpi-card" style={{flex:1}}><span className="mkt-kpi-label">Total envíos</span><strong className="mkt-kpi-value" style={{color:'#60a5fa'}}>{filas.reduce(function(s,f){return s+(f.cantidad_telefonos||1);},0)}</strong></div>
          {cfg3&&cfg3.tiene_boton_pago&&<div className="mkt-kpi-card" style={{flex:1}}><span className="mkt-kpi-label">Botón pago</span><strong className="mkt-kpi-value" style={{color:'#4ade80'}}>Sí</strong></div>}
        </div>
        {nErr>0&&<div style={{padding:'10px 16px',background:'rgba(239,68,68,.1)',border:'1px solid rgba(239,68,68,.2)',borderRadius:8,marginBottom:16,fontSize:12,color:'#fca5a5'}}>⚠️ {nErr} fila{nErr>1?'s':''} con errores — corregí haciendo clic en las celdas rojas</div>}
        <div className="mkt-card"><div className="mkt-table-wrap" style={{maxHeight:500,overflow:'auto'}}>
          <table className="mkt-table"><thead><tr><th style={{width:40}}>#</th>{cols.map(function(c){return <th key={c}>{c}</th>;})}<th style={{width:40}}></th></tr></thead>
          <tbody>{filas.map(function(f,idx){return(
            <tr key={idx}>
              <td style={{color:'var(--muted)',fontSize:11}}>{idx+1}</td>
              {cols.map(function(c){var err=f._errores&&f._errores.includes(c);var displayVal=f[c]||'';
                // FIX: Mostrar teléfonos múltiples de forma legible
                if(c==='telefono'&&f.cantidad_telefonos>1){displayVal=f.telefonos_normalizados[0].slice(-10)+' (+'+(f.cantidad_telefonos-1)+' más)';}
                else if(c==='telefono'&&f.telefono_normalizado){displayVal='✓ '+f.telefono_normalizado;}
                return(
                <td key={c} style={{padding:2}}><input value={displayVal} onChange={function(e){edit(idx,c,e.target.value);}} style={{width:'100%',background:err?'rgba(239,68,68,.15)':c==='telefono'&&f.cantidad_telefonos>1?'rgba(99,102,241,.1)':'transparent',border:err?'1px solid rgba(239,68,68,.4)':c==='telefono'&&f.cantidad_telefonos>1?'1px solid rgba(99,102,241,.3)':'1px solid transparent',borderRadius:4,padding:'4px 6px',fontSize:12,color:'var(--text)',outline:'none'}}/></td>
              );})}
              <td><button onClick={function(){setFilas(filas.filter(function(_,i){return i!==idx;}));}} style={{background:'none',border:'none',cursor:'pointer',color:'var(--muted)',fontSize:14}} title="Eliminar">×</button></td>
            </tr>
          );})}</tbody></table>
        </div></div>
        <div style={{marginTop:16,display:'flex',justifyContent:'flex-end',gap:8}}>
          <button className="mkt-btn" onClick={function(){setPaso(2);setFilas([]);}}>Cancelar</button>
          <button className="mkt-btn primary" disabled={filas.length===0} onClick={function(){setPaso(4);}}>Confirmar y enviar ({filas.reduce(function(s,f){return s+(f.cantidad_telefonos||1);},0)} envíos) →</button>
        </div>
      </div>
    );
  }

  // PASO 4
  if(paso===4){
    var cfg4=hitosConfig?hitosConfig[hito]:null;
    if(progreso&&progreso.estado==='completado') return(
      <div className="mkt-card" style={{padding:40,textAlign:'center'}}>
        <div style={{fontSize:48,marginBottom:16}}>✅</div>
        <h2 style={{fontSize:20,marginBottom:8}}>Campaña enviada</h2>
        <p style={{color:'var(--muted)',marginBottom:20}}>{progreso.enviados} enviados • {progreso.fallidos} fallidos de {progreso.total}</p>
        <div style={{display:'flex',gap:8,justifyContent:'center'}}>
          <button className="mkt-btn primary" onClick={onCreada}>Ver campañas</button>
          <button className="mkt-btn" onClick={function(){setPaso(1);setFilas([]);setErrores([]);setHito('');setNombre('');setProgreso(null);setMapInfo(null);}}>Nueva campaña</button>
        </div>
      </div>
    );
    if(progreso&&progreso.estado==='error') return(
      <div className="mkt-card" style={{padding:40,textAlign:'center'}}>
        <div style={{fontSize:48,marginBottom:16}}>❌</div>
        <h2 style={{fontSize:20,marginBottom:8}}>Error en el envío</h2>
        <p style={{color:'#fca5a5',marginBottom:20}}>{progreso.mensaje}</p>
        <button className="mkt-btn" onClick={function(){setProgreso(null);}}>Reintentar</button>
      </div>
    );

    async function enviar(){
      setEnviando(true);setProgreso({estado:'iniciando'});
      try{
        var totalEnvios=filas.reduce(function(s,f){return s+(f.cantidad_telefonos||1);},0);
        var r=await authFetch(API_URL+'/api/outbound/ejecutar',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({hito:hito,nombre:nombre,filas:filas.map(function(f){var c=Object.assign({},f);delete c._errores;return c;})})});
        var d=await r.json();
        d.ok?setProgreso({estado:'completado',enviados:d.enviados,fallidos:d.fallidos,total:d.total_envios||totalEnvios,campana_id:d.campana_id}):setProgreso({estado:'error',mensaje:d.error||'Error desconocido'});
      }catch(e){setProgreso({estado:'error',mensaje:e.message});}
      setEnviando(false);
    }

    return(
      <div>
        <button className="mkt-btn" onClick={function(){setPaso(3);}} style={{marginBottom:16}}>← Volver</button>
        <div className="mkt-card" style={{padding:24}}>
          <h3 style={{fontSize:16,marginBottom:16}}>Confirmar envío</h3>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:20}}>
            <div style={{padding:12,background:'var(--card-hover)',borderRadius:8}}><div style={{fontSize:11,color:'var(--muted)',marginBottom:4}}>Hito</div><div style={{fontSize:14,fontWeight:600}}>{cfg4?cfg4.nombre:hito}</div></div>
            <div style={{padding:12,background:'var(--card-hover)',borderRadius:8}}><div style={{fontSize:11,color:'var(--muted)',marginBottom:4}}>Contactos</div><div style={{fontSize:14,fontWeight:600}}>{filas.length}</div></div>
            <div style={{padding:12,background:'var(--card-hover)',borderRadius:8}}><div style={{fontSize:11,color:'var(--muted)',marginBottom:4}}>Total envíos</div><div style={{fontSize:14,fontWeight:600,color:'#60a5fa'}}>{filas.reduce(function(s,f){return s+(f.cantidad_telefonos||1);},0)}</div></div>
            <div style={{padding:12,background:'var(--card-hover)',borderRadius:8}}><div style={{fontSize:11,color:'var(--muted)',marginBottom:4}}>Botón pago</div><div style={{fontSize:14,fontWeight:600,color:cfg4&&cfg4.tiene_boton_pago?'#4ade80':'var(--muted)'}}>{cfg4&&cfg4.tiene_boton_pago?'Sí — genera tokens':'No'}</div></div>
            <div style={{padding:12,background:'var(--card-hover)',borderRadius:8}}><div style={{fontSize:11,color:'var(--muted)',marginBottom:4}}>Categoría</div><div style={{fontSize:14,fontWeight:600}}>{cfg4?cfg4.categoria_meta:'-'}</div></div>
          </div>
          {cfg4&&cfg4.tiene_boton_pago&&<div style={{padding:'10px 16px',background:'rgba(34,197,94,.08)',border:'1px solid rgba(34,197,94,.15)',borderRadius:8,marginBottom:16,fontSize:12,color:'#86efac'}}>💳 Se generarán {filas.reduce(function(s,f){return s+(f.cantidad_telefonos||1);},0)} tokens (1 por número) — URL: dashboard.weconnect.ar/pagar/TOKEN</div>}
          <div style={{padding:'10px 16px',background:'rgba(239,68,68,.08)',border:'1px solid rgba(239,68,68,.15)',borderRadius:8,marginBottom:20,fontSize:12,color:'#fca5a5'}}>⚠️ Se enviarán {filas.reduce(function(s,f){return s+(f.cantidad_telefonos||1);},0)} mensajes de WhatsApp ({filas.length} clientes). No se puede deshacer.</div>
          <div style={{display:'flex',justifyContent:'flex-end',gap:8}}>
            <button className="mkt-btn" onClick={function(){setPaso(3);}}>Volver a editar</button>
            <button className="mkt-btn primary" disabled={enviando} onClick={enviar}>{enviando?'Enviando...':'🚀 Confirmar envío'}</button>
          </div>
        </div>
        {progreso&&progreso.estado==='iniciando'&&<div className="mkt-card" style={{padding:20,marginTop:16,textAlign:'center'}}><div style={{fontSize:14,marginBottom:12}}>Enviando mensajes...</div><div style={{height:6,background:'var(--border-light)',borderRadius:3,overflow:'hidden'}}><div style={{height:'100%',background:'var(--accent)',borderRadius:3,width:'100%',animation:'pulse 1.5s ease-in-out infinite'}}></div></div></div>}
      </div>
    );
  }
  return null;
}

// ═══ CAMPAÑAS TAB ═══
function CampanasTab({campanas,onReload}){
  var [det,setDet]=useState(null);
  var [detData,setDetData]=useState(null);
  var [load,setLoad]=useState(false);

  async function ver(c){setDet(c);setLoad(true);try{var r=await authFetch(API_URL+'/api/outbound/campanas/'+c.id);var d=await r.json();if(d.ok)setDetData(d);}catch(e){console.error(e);}setLoad(false);}

  async function eliminar(c){
    if(!window.confirm('¿Eliminar la campaña "' + (c.nombre||c.template_name) + '"?\n\nSe borrarán todos los envíos y tokens asociados. Esta acción no se puede deshacer.')) return;
    try{
      var r=await authFetch(API_URL+'/api/outbound/campanas/'+c.id,{method:'DELETE'});
      var d=await r.json();
      if(d.ok){alert('Campaña eliminada');onReload();}
      else{alert('Error: '+(d.error||'No se pudo eliminar'));}
    }catch(e){alert('Error de conexión');console.error(e);}
  }

  if(det&&detData) return(
    <div>
      <button className="mkt-btn" onClick={function(){setDet(null);setDetData(null);}} style={{marginBottom:16}}>← Volver</button>
      <div style={{display:'flex',gap:12,marginBottom:16}}>
        {[{l:'Total',v:detData.metricas.total,c:'var(--accent)'},{l:'Enviados',v:detData.metricas.enviados,c:'var(--green)'},{l:'Fallidos',v:detData.metricas.fallidos,c:'var(--red)'},{l:'Clicks',v:detData.metricas.clicks,c:'#4ade80'},{l:'Tasa clic',v:detData.metricas.tasa_click,c:'#fbbf24'}].map(function(m){return <div key={m.l} className="mkt-kpi-card" style={{flex:1}}><span className="mkt-kpi-label">{m.l}</span><strong className="mkt-kpi-value" style={{color:m.c}}>{m.v}</strong></div>;})}
      </div>
      <div className="mkt-card"><h3 style={{fontSize:14,padding:'12px 16px',borderBottom:'1px solid var(--border-light)'}}>Envíos ({detData.envios.length})</h3>
        <div className="mkt-table-wrap" style={{maxHeight:400,overflow:'auto'}}><table className="mkt-table"><thead><tr><th>Teléfono</th><th>Estado</th><th>Error</th><th>Enviado</th></tr></thead>
        <tbody>{detData.envios.map(function(e){return <tr key={e.id}><td>{e.telefono}</td><td><span className="mkt-badge" style={{background:(ESTADO_ENVIO_COLORS[e.estado]||'var(--muted)')+'22',color:ESTADO_ENVIO_COLORS[e.estado]||'var(--muted)',border:'1px solid '+(ESTADO_ENVIO_COLORS[e.estado]||'var(--muted)')+'44'}}>{e.estado}</span></td><td style={{fontSize:11,color:'var(--muted)'}}>{e.error_mensaje||'-'}</td><td style={{fontSize:11}}>{e.enviado_en?new Date(e.enviado_en).toLocaleString('es-AR'):'-'}</td></tr>;})}{detData.envios.length===0&&<tr><td colSpan="4" style={{textAlign:'center',color:'var(--muted)'}}>Sin envíos</td></tr>}</tbody></table></div></div>
    </div>
  );

  return(
    <div className="mkt-card"><div className="mkt-table-wrap"><table className="mkt-table"><thead><tr><th>Fecha</th><th>Nombre</th><th>Template</th><th>Estado</th><th>Contactos</th><th>Enviados</th><th>Fallidos</th><th></th></tr></thead>
    <tbody>{campanas.map(function(c){return <tr key={c.id}><td style={{fontSize:12}}>{c.created_at?new Date(c.created_at).toLocaleDateString('es-AR'):'-'}</td><td style={{fontWeight:600}}>{c.nombre||'-'}</td><td><span className="mkt-badge">{c.template_name}</span></td><td><span className="mkt-badge" style={{background:(ESTADO_CAMPANA_COLORS[c.estado]||'var(--muted)')+'22',color:ESTADO_CAMPANA_COLORS[c.estado]||'var(--muted)',border:'1px solid '+(ESTADO_CAMPANA_COLORS[c.estado]||'var(--muted)')+'44'}}>{c.estado}</span></td><td>{c.total_contactos}</td><td style={{color:'var(--green)'}}>{c.enviados}</td><td style={{color:c.fallidos>0?'var(--red)':'var(--muted)'}}>{c.fallidos}</td><td><button className="mkt-btn-sm" onClick={function(){ver(c);}} disabled={load}>Ver</button> <button className="mkt-btn-sm" onClick={function(){eliminar(c);}} disabled={load} style={{background:'#ef444422',color:'#ef4444',border:'1px solid #ef444444',marginLeft:4}}>Eliminar</button></td></tr>;})}{campanas.length===0&&<tr><td colSpan="8" style={{textAlign:'center',color:'var(--muted)'}}>No hay campañas</td></tr>}</tbody></table></div></div>
  );
}

// ═══ REFERENCIA HITOS ═══
function HitosTab({hitosConfig}){
  if(!hitosConfig) return <div style={{padding:20,color:'var(--muted)'}}>Cargando...</div>;
  var grupos={cobranza:{label:'Ciclo de cobranza',color:'#6366f1',hitos:['hito_2_dash','hito_3_dash','hito_4_dash','hito_5_dash','hito_6_dash','hito_7_dash','hito_8_dash','hito_9_dash','hito_10_dash','ciclo_vigente_dash']},reconexion:{label:'Reconexión',color:'#22c55e',hitos:['extra_reconexion_dash','extra_volver_dash']},avisos:{label:'Avisos operativos',color:'#f59e0b',hitos:['tareas_mantenimiento_dash','corte_electrico_dash','aviso_general_dash']}};
  return(
    <div>
      <div style={{fontSize:12,color:'var(--muted)',marginBottom:16}}>Columnas por hito. <span style={{color:'#a5b4fc'}}>Azul</span> = base, <span style={{color:'#fbbf24'}}>amarillo</span> = variable.</div>
      {Object.entries(grupos).map(function(e){var k=e[0],g=e[1];return(
        <div key={k} style={{marginBottom:24}}>
          <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:10}}><div style={{width:3,height:14,borderRadius:2,background:g.color}}></div><span style={{fontSize:11,fontWeight:600,color:'var(--muted)',textTransform:'uppercase',letterSpacing:1}}>{g.label}</span></div>
          {g.hitos.map(function(h){var c=hitosConfig[h];if(!c)return null;return(
            <div key={h} className="mkt-card" style={{padding:'12px 16px',marginBottom:8}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
                <span style={{fontSize:13,fontWeight:600}}>{c.nombre}</span>
                <div style={{display:'flex',gap:4}}>
                  {c.tiene_boton_pago?<span style={{fontSize:10,padding:'2px 6px',borderRadius:4,background:'rgba(34,197,94,.1)',color:'#4ade80',border:'1px solid rgba(34,197,94,.2)',fontWeight:600}}>Botón pago</span>:<span style={{fontSize:10,padding:'2px 6px',borderRadius:4,background:'rgba(255,255,255,.04)',color:'#6b6960',fontWeight:600}}>Sin botón</span>}
                  <span style={{fontSize:10,padding:'2px 6px',borderRadius:4,background:'rgba(99,102,241,.12)',color:'#818cf8',fontWeight:600}}>{c.categoria_meta}</span>
                </div>
              </div>
              <div style={{display:'flex',gap:4,flexWrap:'wrap'}}>
                {c.columnas_base.map(function(col){return <span key={col} style={{fontSize:11,padding:'3px 8px',borderRadius:5,background:'rgba(99,102,241,.15)',color:'#a5b4fc',border:'1px solid rgba(99,102,241,.25)'}}>{col}</span>;})}
                {c.columnas_variables.map(function(col){return <span key={col} style={{fontSize:11,padding:'3px 8px',borderRadius:5,background:'rgba(234,179,8,.1)',color:'#fbbf24',border:'1px solid rgba(234,179,8,.2)'}}>{col}</span>;})}
              </div>
            </div>
          );})}
        </div>
      );})}
      <div className="mkt-card" style={{padding:16}}><div style={{display:'grid',gridTemplateColumns:'repeat(3, 1fr)',gap:12,textAlign:'center'}}>
        <div><div style={{fontSize:28,fontWeight:600,color:'#818cf8'}}>15</div><div style={{fontSize:11,color:'var(--muted)'}}>Templates</div></div>
        <div><div style={{fontSize:28,fontWeight:600,color:'#4ade80'}}>7</div><div style={{fontSize:11,color:'var(--muted)'}}>Con botón pago</div></div>
        <div><div style={{fontSize:28,fontWeight:600,color:'#fbbf24'}}>8</div><div style={{fontSize:11,color:'var(--muted)'}}>Sin botón</div></div>
      </div></div>
    </div>
  );
}

export default React.memo(OutboundPanel);
